import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { HttpService } from './http.service';
import { AuthService } from './auth.service';
import { getAuthCookie, setAuthCookie } from '../utils/auth-session.util';
import { API_CONFIG } from '../config/api.config';
import { ITokenRefreshService } from '../tokens/token-refresh.token';
import { SessionExpiryWarningService } from './session-expiry/session-expiry-warning.service';
import { authFlowDebug } from '../utils/auth-flow-debug.util';

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService implements ITokenRefreshService {
  private readonly http = inject(HttpService);
  private readonly authService = inject(AuthService);
  private readonly sessionExpiryWarning = inject(SessionExpiryWarningService);

  private isRefreshing = false;
  private readonly refreshTokenSubject = new BehaviorSubject<string | null | false>(null);

  refreshTokens(): Observable<string> {
    if (this.isRefreshing) {
      authFlowDebug.log('token_refresh_queued', 'TokenRefreshService.refreshTokens', {
        detail: 'Refresh already in progress; waiting for result.',
      });
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) =>
          token === false ? throwError(() => new Error('Token refresh failed')) : [token as string],
        ),
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    const refreshToken =
      localStorage.getItem('refreshToken') ?? getAuthCookie('refreshToken');

    if (!refreshToken) {
      this.isRefreshing = false;
      authFlowDebug.log(
        'token_refresh_skipped_no_refresh_token',
        'TokenRefreshService.refreshTokens',
        {
          reason: 'INVALID_REFRESH_TOKEN',
          level: 'warn',
        },
      );
      this.authService.logout({
        source: 'TokenRefreshService.refreshTokens',
        reason: 'no_refresh_token',
      });
      return throwError(() => new Error('No refresh token available'));
    }

    authFlowDebug.log('token_refresh_started', 'TokenRefreshService.refreshTokens', {
      data: { refreshUrl: API_CONFIG.auth.refresh },
    });

    const payload = { refreshToken };
    const refreshUrl = API_CONFIG.auth.refresh;

    return this.http.post<any>(refreshUrl, payload).pipe(
      switchMap((res) => {
        const newAccessToken = res?.data?.accessToken;
        const newRefreshToken = res?.data?.refreshToken;

        if (res?.status && newAccessToken) {
          localStorage.setItem('brickhub_customer_auth', newAccessToken);
          localStorage.setItem('accessToken', newAccessToken);
          setAuthCookie('brickhub_customer_auth', newAccessToken);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
            setAuthCookie('refreshToken', newRefreshToken);
          }
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newAccessToken);
          this.sessionExpiryWarning.reschedule();
          authFlowDebug.log('token_refresh_success', 'TokenRefreshService.refreshTokens', {
            data: {
              hasNewRefreshToken: !!newRefreshToken,
            },
          });
          return [newAccessToken];
        } else {
          authFlowDebug.log('token_refresh_failed', 'TokenRefreshService.refreshTokens', {
            detail: 'Invalid refresh response shape',
            level: 'error',
          });
          return throwError(() => new Error('Invalid refresh response'));
        }
      }),
      catchError((err) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(false);
        authFlowDebug.logHttpError(
          'token_refresh_failed',
          'TokenRefreshService.refreshTokens',
          err,
        );
        this.authService.logout({
          source: 'TokenRefreshService.refreshTokens',
          reason: 'refresh_failed',
        });
        return throwError(() => err);
      }),
    );
  }
}
